import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, ExternalLink, CheckCheck, MailX, MailCheck, AtSign, Award, MessageCircle, CalendarDays, Newspaper, Reply, FileText, Flag, Rocket } from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = { mention: AtSign, comment: MessageCircle, certificate: Award, digest: Newspaper, document: FileText, feedback: Flag, onboarding: Rocket };

function InlineReply({ n, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.stopPropagation();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/highlights/${n.meta.highlight_id}/comments`, { text, mentions: n.actor_id ? [n.actor_id] : [] });
      if (!n.read) await api.post(`/notifications/${n.id}/read`);
      setText("");
      toast.success(`Reply sent to ${n.actor_name}`);
      onSent();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send reply"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
      <Input data-testid={`inbox-reply-input-${n.id}`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(e)} placeholder={`Reply to ${n.actor_name?.split(" ")[0]}…`} className="h-8 text-xs" />
      <Button data-testid={`inbox-reply-send-${n.id}`} size="sm" onClick={send} disabled={busy || !text.trim()} className="h-8 px-2.5 bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><Reply className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

export default function ReminderBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isManager = user?.role === "manager";
  const [rem, setRem] = useState({ items: [], unread: 0 });
  const [notif, setNotif] = useState({ items: [], unread: 0 });

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setNotif(r.data)).catch(() => {});
    if (isManager) api.get("/reminders").then((r) => setRem(r.data)).catch(() => {});
  }, [isManager]);
  useEffect(() => { load(); const t = setInterval(load, 3 * 60 * 1000); return () => clearInterval(t); }, [load]);

  const unread = rem.unread + notif.unread;
  const readAll = async () => {
    await Promise.all([api.post("/notifications/read-all"), isManager ? api.post("/reminders/read-all") : Promise.resolve()]);
    load();
  };
  const openNotif = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    load();
    if (n.link) nav(n.link);
  };
  const emailed = rem.items.some((i) => i.email_status === "sent");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid="reminder-bell" variant="outline" className="relative border-slate-300 px-2.5" aria-label="Notifications">
          <Bell className="w-4 h-4" />
          {unread > 0 && <span data-testid="reminder-unread" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F5A623] text-[#0A2A5E] text-[10px] font-bold flex items-center justify-center">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display font-bold text-[#0A2A5E] text-sm">Inbox</div>
          {unread > 0 && <Button size="sm" variant="ghost" data-testid="reminders-read-all" onClick={readAll} className="text-xs"><CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all read</Button>}
        </div>
        <div className="max-h-[28rem] overflow-y-auto">
          <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><AtSign className="w-3 h-3" /> Mentions & activity</div>
          {notif.items.length === 0 && <div className="px-3 pb-3 text-xs text-slate-500" data-testid="notifications-empty">No mentions or activity yet.</div>}
          <div className="divide-y divide-slate-100">
            {notif.items.slice(0, 15).map((n) => {
              const Icon = KIND_ICON[n.kind] || MessageCircle;
              return (
                <div key={n.id} data-testid={`notification-${n.id}`} className={`p-3 text-sm ${n.read ? "bg-white" : "bg-[#DBEAFE]/50"}`}>
                  <button onClick={() => openNotif(n)} className="w-full text-left flex gap-2 hover:opacity-80">
                    <Icon className="w-4 h-4 text-[#0A2A5E] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-semibold text-[#0A2A5E] leading-snug">{n.title}</div>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <div className="text-[11px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  </button>
                  {(n.kind === "mention" || n.kind === "comment") && n.meta?.highlight_id && <InlineReply n={n} onSent={load} />}
                </div>
              );
            })}
          </div>

          {isManager && (
            <>
              <div className="px-3 pt-3 pb-1 border-t border-slate-200 text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center justify-between">
                <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Compliance reminders</span>
                <span className="normal-case tracking-normal font-normal flex items-center gap-1 text-slate-400">{emailed ? <><MailCheck className="w-3 h-3" /> also emailed</> : <><MailX className="w-3 h-3" /> in-app</>}</span>
              </div>
              {rem.items.length === 0 && <div className="px-3 pb-3 text-xs text-slate-500" data-testid="reminders-empty">No reminders yet.</div>}
              <div className="divide-y divide-slate-100">
                {rem.items.map((r) => (
                  <div key={r.id} data-testid={`reminder-${r.id}`} className={`p-3 text-sm ${r.read ? "bg-white" : "bg-[#FFF7E8]"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-[#0A2A5E] leading-snug">{r.title}</div>
                      {!r.read && <button data-testid={`reminder-read-${r.id}`} onClick={async () => { await api.post(`/reminders/${r.id}/read`); load(); }} className="text-[10px] uppercase font-bold text-[#7C3F00] hover:underline whitespace-nowrap">Mark read</button>}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{r.message}</p>
                    <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate-500">
                      <span>{r.agency}</span><span>·</span><span>{r.start} → {r.end}</span>
                      <a href={r.source_url} target="_blank" rel="noreferrer" className="ml-auto text-[#0A2A5E] hover:underline inline-flex items-center gap-1">Source <ExternalLink className="w-3 h-3" /></a>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
